/**
 * Flow editor — visual/display settings types + their defaults.
 *
 * Extracted from flowStore.ts so the settings shapes (gameplay, interface,
 * particles, phone, SFX) and default values stand on their own. Re-exported
 * by flowStore for backward-compatible imports. Self-contained (no deps).
 */

// ── Visual / display settings ─────────────────────────────────────────────────

export interface FlowGameplaySettings {
  /** Per-lane hex colour strings, indices 0–4 matching FLOW_LANE_COUNT (5).
   *  Persisted 4-entry copies from older builds are padded on load. */
  laneColors: string[];
  /** Multiplier on the raw note mesh scale (1.0 = default). */
  noteScale: number;
  /** Emissive glow intensity of the note material (0–1). */
  noteEmissive: number;
  /** PBR metalness of the note material (0–1). */
  noteMetalness: number;
  /** PBR roughness of the note material (0–1). */
  noteRoughness: number;
  /** Trail / hold-note width in world units.
   *  ≤ 0.10  → thin Line ribbon (drei <Line lineWidth>)
   *  > 0.10  → flat quad-strip plane (Magic-Tiles style, up to ~full lane).
   *  Range: 0.02 – 0.90.  Default 0.055 (thin line). */
  trailWidth: number;
  /** Note head height above the floor/timeline (world units, added to the
   *  base resting height). 0 = sits on the deck; higher = floats above it. */
  noteHeight: number;
  /** Animated highlight sweep on rails. When on, it only runs within ~3 beats
   *  of the editor plane (reviewer perf ask); off = always-flat ribbons. */
  trailSweepVfx: boolean;
}

export interface FlowInterfaceSettings {
  /** Show the white strike bar across the lane. */
  strikeBarVisible: boolean;
  /** Thickness (depth along the track, world units) of the strike bar line. */
  strikeBarThickness: number;
  /** Strike bar colour (hex) — used only when strikeBarUseAccent is false. */
  strikeBarColor: string;
  /** When true (default), the strike bar follows the design-system accent
   *  colour instead of strikeBarColor — keeps it in sync with the theme. */
  strikeBarUseAccent: boolean;
  /** Show the lane key-pad ("trigger") pill outlines at the strike line. */
  keypadVisible: boolean;
  /** Opacity of the lane key-pads at the strike line (0–1). */
  keypadOpacity: number;
  /** Show the hover placement-preview ring on the snapped cell under the cursor. */
  placeMarkerVisible: boolean;
  /** Opacity of the placement-preview ring (0–1). */
  placeMarkerOpacity: number;
  /** Show beat-number labels along the runway. */
  beatMarkersVisible: boolean;
  /** Opacity of lane divider lines (0–1). */
  dividerOpacity: number;
  /** Play the place (drop/bounce) and delete (shatter) note animations. */
  noteAnimations: boolean;
  /** Delete "shatter" burst specifically — clearing many notes at once gets
   *  visually noisy, so it has its own switch (noteAnimations still gates it). */
  deleteVfx: boolean;
  /** Fade the snap/beat lines out while the music plays. OFF by default (user
   *  feedback: mappers want the grid visible during playback to track timing);
   *  turn on to get the old clean-stage look while playing. */
  fadeBeatLinesOnPlay: boolean;
  /** Minimap beat spacing multiplier (0.5–2). Higher = notes spread apart. */
  minimapZoom: number;
  /** On pause, snap the playhead to the nearest snap-division line. */
  snapOnPause: boolean;
  /** Song-waveform visualizer (the sprite strips down both runway edges). */
  waveformVisible: boolean;  // master on/off
  waveformColor: string;     // hex bar colour
  waveformOpacity: number;   // 0–1
  waveformDensity: number;   // palito count along the whole song (more = thinner/denser)
  waveformHeight: number;    // outward reach (world units) of the strip = its visual height
  waveformGap: number;       // distance (world units) from the runway edge to the strip
  /** Beat-number label chips along the runway. */
  beatLabelText: string;     // number colour (hex)
  beatLabelBg: string;       // container/background colour (hex)
  beatLabelOutline: string;  // border/outline colour (hex)
  /** Show the .visualizer scene canvas behind the timeline strip. */
  timelineVisualizerEnabled: boolean;
  /** What drives the visualizer's reactivity: the live audio amplitude, or the
   *  chart notes crossing the playhead. */
  visualizerReactTo: 'audio' | 'notes';
  /** Align the visualizer's focal point (its "vanishing point") with the 3D
   *  highway camera's, so the background and the runway feel like one scene
   *  instead of two separate layers. */
  visualizerAlignCamera: boolean;
  /** Visualizer camera placement: 'auto' aligns to the highway vanishing point;
   *  'manual' lets the user position it with the X/Y/Z offsets below. */
  visualizerCameraMode: 'auto' | 'manual';
  /** Manual-mode offsets: X/Y are screen-percent pan, Z is zoom (scale). */
  visualizerCamX: number;
  visualizerCamY: number;
  visualizerCamZ: number;
}

/** Silhouette of a spark burst. `cone` shoots everything upward off the strike
 *  bar; `box` spreads uniformly so the burst reads as a cube. */
export type FlowSparkShape = 'cone' | 'box';

export interface FlowParticleSettings {
  /** Number of sparks spawned per hit. */
  sparksPerHit: number;
  /** How long (seconds) each spark lives. */
  sparkLifetime: number;
  /** Launch speed, LOW end of the range (units/sec). */
  sparkSpeed: number;
  /** Launch speed, HIGH end. Every spark picks a random speed between the two,
   *  which is what gives a burst depth instead of a flat expanding shell. */
  sparkSpeedMax: number;
  /** Downward pull acceleration (units/sec²). */
  sparkGravity: number;
  /** Scale of each spark cube (world units). */
  sparkSize: number;
  /** Burst silhouette — see FlowSparkShape. */
  sparkShape: FlowSparkShape;
  /** Size of the cube sparks are born inside (box shape only, world units). */
  sparkSpread: number;
  /** Wobble strength. 0 = straight ballistic paths. */
  sparkTurbulence: number;
}

export const DEFAULT_FLOW_GAMEPLAY: FlowGameplaySettings = {
  laneColors: ['#cf66ff', '#a347ff', '#7a70ff', '#5f5cff', '#4de1ff'],
  noteScale: 1.75,
  noteEmissive: 0.54,
  noteMetalness: 0.98,
  noteRoughness: 0.46,
  trailWidth: 0.62,
  noteHeight: 0,
  trailSweepVfx: true,
};

export const DEFAULT_FLOW_INTERFACE: FlowInterfaceSettings = {
  strikeBarVisible: true,
  strikeBarThickness: 0.09,
  strikeBarColor: '#ffffff',
  strikeBarUseAccent: true,
  keypadVisible: true,
  keypadOpacity: 0.4,
  placeMarkerVisible: true,
  placeMarkerOpacity: 0.7,
  beatMarkersVisible: true,
  dividerOpacity: 0.66,
  noteAnimations: true,
  deleteVfx: true,
  fadeBeatLinesOnPlay: false,
  minimapZoom: 1,
  snapOnPause: true,
  waveformVisible: true,
  waveformColor: '#9313fb',
  waveformOpacity: 0.54,
  waveformDensity: 6000,
  waveformHeight: 3.1,
  waveformGap: 0,
  beatLabelText: '#f4f0ff',
  beatLabelBg: '#0a0520',
  beatLabelOutline: '#d946ef',
  timelineVisualizerEnabled: false,
  visualizerReactTo: 'audio',
  visualizerAlignCamera: false,
  visualizerCameraMode: 'auto',
  visualizerCamX: 0,
  visualizerCamY: 0,
  visualizerCamZ: 1,
};

export const DEFAULT_FLOW_PARTICLES: FlowParticleSettings = {
  sparksPerHit: 8,
  sparkLifetime: 0.55,
  sparkSpeed: 2.2,
  sparkSpeedMax: 3.4,
  sparkGravity: 5.5,
  sparkSize: 0.06,
  sparkShape: 'box',
  sparkSpread: 0.35,
  sparkTurbulence: 0,
};

/** Phone (mobile player) tuning. These drive the QR-launched gameplay view.
 *  They ride along in the QR URL so a phone (which doesn't share localStorage
 *  with the editor) picks them up. */
export interface FlowPhoneSettings {
  /** Note approach speed (NJS) multiplier. 1 = base; higher = faster. */
  noteSpeed: number;
  /** Note size multiplier on top of the gameplay noteScale. 1 = base. */
  noteSize: number;
  /** Track tilt: camera pitch delta in DEGREES from the default view.
   *  0 = current perspective; positive = more top-down. */
  trackTilt: number;
  /** Camera vertical pan in world units (raise/lower the view). 0 = default. */
  camHeight: number;
  /** Camera distance offset in world units (zoom out/in). 0 = default. */
  camDist: number;
}
export const DEFAULT_FLOW_PHONE: FlowPhoneSettings = {
  noteSpeed: 1,
  noteSize: 1,
  trackTilt: 0,
  camHeight: 0,
  camDist: 0,
};

/** Editor sound-effects preferences (scroll tick + note hits). */
export type FlowScrollSound = 'classic' | 'click' | 'soft';
/** Voice played when a note is placed / deleted on the timeline. */
export type FlowPlaceSound = 'pop' | 'blip' | 'thock' | 'quack' | 'none';
export type FlowDeleteSound = 'shatter' | 'zap' | 'thud' | 'none';
/** Note-hit VOICE: the synthesised tone, or a recorded sample. 'clap' is the
 *  hit sound from the Synths Riders desktop (Unity) editor. */
export type FlowNoteSound = 'tone' | 'clap';
export interface FlowSfxSettings {
  /** Which scroll-tick voice plays when scrubbing the wheel. */
  scrollSound: FlowScrollSound;
  /** Scroll-tick volume (0..1), multiplies the master SFX volume. */
  scrollVolume: number;
  /** Which voice a note hit uses (synth tone vs sampled clap). */
  noteSound: FlowNoteSound;
  /** Note-hit volume (0..1), multiplies the master SFX volume. */
  noteVolume: number;
  /** true → one sound for all lanes; false → 4 per-lane tones (Do-Re-Mi-Fa). */
  laneSoundUniform: boolean;
  /** Voice when a note is placed on the timeline. */
  placeSound: FlowPlaceSound;
  /** Voice when a note is deleted from the timeline. */
  deleteSound: FlowDeleteSound;
  /** Own volumes (0..1) for the place / delete voices. */
  placeVolume: number;
  deleteVolume: number;
}
export const DEFAULT_FLOW_SFX: FlowSfxSettings = {
  scrollSound: 'classic',
  scrollVolume: 1,
  noteSound: 'tone',
  noteVolume: 1,
  // Uniform pitch across lanes BY DEFAULT (user feedback) — the per-lane
  // Do-Re-Mi-Fa-Sol scale stays available as the opt-in.
  laneSoundUniform: true,
  placeSound: 'pop',
  deleteSound: 'shatter',
  placeVolume: 1,
  deleteVolume: 1,
};

// ── Visualizer scene (loaded from .visualizer files) ─────────────────────────

export interface VisualizerSceneParams {
  primitive?: string;
  primitiveEnabled?: boolean;
  reactivity?: number;
  rotationSpeed?: number;
  hue?: number;
  density?: number;
  pointSize?: number;
  pointShape?: string;
  pointSolid?: boolean;
  bloom?: number;
  trails?: number;
  bgColorStops?: string[];
  bgGradientAngle?: number;
  bgColorMode?: string;
  backgroundType?: string;
  [key: string]: unknown;
}

export interface VisualizerSceneLab {
  palette?: string[];
  intensity?: number;
  active?: boolean;
}

export interface VisualizerScene {
  id: string;
  name: string;
  params: VisualizerSceneParams;
  primitiveLab?: VisualizerSceneLab;
}

/** One scene clip on a .vizproject timeline: a scene look pinned to an absolute
 *  time window (seconds) on the track. */
export interface VizClip {
  startTime: number;
  duration: number;
  scene: VisualizerScene;
}

